import { useMemo, useState } from 'react';
import { Card, CardHeader } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import {
  useVisibilityChats,
  useVisibilityTags,
  useProbeTags,
  useSetProbeTags,
} from '../../../../hooks/useVisibility';
import { useCitationProbes, useDeleteCitationProbe } from '../../../../hooks/useMeasurement';
import { useFilters } from '../filters';
import { PROVIDER_LABELS } from '../format';
import { Table, THead, Th, Td } from '../../../../components/ui/Table';
import { EmptyState, LoadingState } from '../../../../components/ui/States';

export function PromptsPage({ onAddProbe }: { onAddProbe: () => void }) {
  const { data: probesResp, isLoading } = useCitationProbes();
  const deleteProbe = useDeleteCitationProbe();
  const probes = useMemo(
    () => (probesResp?.probes ?? []).filter((p) => p.isActive),
    [probesResp],
  );
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <Card padding="md">
        <CardHeader
          title="Prompts"
          description={`${probes.length} active prompts probing AI engines daily`}
          action={<Button size="sm" variant="outline" onClick={onAddProbe}>Add prompt</Button>}
        />
        {isLoading ? (
          <LoadingState />
        ) : probes.length === 0 ? (
          <ZeroPrompts onAdd={onAddProbe} />
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="overflow-x-auto">
              <Table>
                <THead>
                  <tr>
                    <Th className="px-3">Query</Th>
                    <Th className="px-3">Category</Th>
                    <Th align="right" className="px-3"></Th>
                  </tr>
                </THead>
                <tbody>
                  {probes.map((p) => (
                    <tr
                      key={p.id}
                      className={`cursor-pointer border-t border-border-light ${selected === p.id ? 'bg-info-light/40' : 'hover:bg-surface-secondary'}`}
                      onClick={() => setSelected(p.id)}
                    >
                      <Td className="px-3">{p.query}</Td>
                      <Td className="px-3"><Badge>{p.category}</Badge></Td>
                      <Td align="right" className="px-3">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); if (confirm(`Remove prompt "${p.query}"?`)) deleteProbe.mutate(p.id); }}
                          className="text-text-tertiary hover:text-error text-body-xs"
                        >
                          Remove
                        </button>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div>
              {selected ? (
                <PromptDetail probeId={selected} />
              ) : (
                <EmptyState compact title="Pick a prompt on the left to see its tags and recent answers." />
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function ZeroPrompts({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="py-10 text-center">
      <p className="text-body-md text-text-primary">No prompts configured yet</p>
      <p className="text-body-sm text-text-tertiary mt-1 max-w-md mx-auto">
        Add prompts that mirror real buyer questions ("Best RFP software for enterprise") — the OS will probe them daily across OpenAI, Claude, Perplexity, and Gemini.
      </p>
      <Button size="sm" variant="primary" onClick={onAdd} className="mt-4">Add your first prompt</Button>
    </div>
  );
}

function PromptDetail({ probeId }: { probeId: string }) {
  const { startDate, endDate } = useFilters();
  const { data, isLoading } = useVisibilityChats(startDate, endDate, { probeId });

  return (
    <div className="space-y-3">
      <ProbeTagEditor probeId={probeId} />
      {isLoading && <LoadingState />}
      {data && data.items.length === 0 && (
        <p className="text-body-sm text-text-tertiary">No answers for this prompt in the selected range.</p>
      )}
      {data && data.items.slice(0, 20).map((c) => (
        <div key={c.id} className="border border-border-light rounded-lg p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-body-xs font-medium">{PROVIDER_LABELS[c.provider] ?? c.provider} · {c.runDate}</span>
            {c.cited
              ? <Badge variant="success">Cited{c.listRank ? ` · #${c.listRank}` : ''}</Badge>
              : <Badge variant="default">Not cited</Badge>}
          </div>
          <p className="text-body-sm text-text-secondary line-clamp-3">{c.preview}…</p>
        </div>
      ))}
    </div>
  );
}

function ProbeTagEditor({ probeId }: { probeId: string }) {
  const { data: allTags } = useVisibilityTags();
  const { data: probeTags } = useProbeTags(probeId);
  const setTags = useSetProbeTags();
  const selected = useMemo(() => new Set((probeTags?.tags ?? []).map((t) => t.id)), [probeTags]);

  function toggle(tagId: string) {
    const next = new Set(selected);
    if (next.has(tagId)) next.delete(tagId);
    else next.add(tagId);
    setTags.mutate({ probeId, tagIds: Array.from(next) });
  }

  return (
    <div className="border border-border-light rounded-lg p-3 bg-surface-secondary/40">
      <div className="flex items-center justify-between mb-2">
        <span className="text-body-xs font-medium text-text-secondary">Intent tags</span>
        <span className="text-body-xs text-text-tertiary">drives recommendation priority</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(allTags?.items ?? []).map((t) => {
          const isOn = selected.has(t.id);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => toggle(t.id)}
              disabled={setTags.isPending}
              className={`text-body-xs rounded-full px-2.5 py-1 border ${isOn ? 'bg-text-primary text-text-inverse border-text-primary' : 'border-border-light text-text-tertiary hover:text-text-primary'}`}
            >
              {t.name}
            </button>
          );
        })}
        {(allTags?.items ?? []).length === 0 && (
          <span className="text-body-xs text-text-tertiary">No tags defined yet.</span>
        )}
      </div>
    </div>
  );
}
